'use client';

import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import {
  oneDark,
  oneLight,
} from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useTheme } from 'next-themes';

import { CodeCopyButton } from './code-block-copy';

interface CodeBlockProps {
  children: string;
  language: string;
}

export function CodeBlock({ children, language }: CodeBlockProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme !== 'light';
  const languageLabel = language ? language.toUpperCase() : 'TEXT';

  return (
    <div
      className={
        isDark
          ? 'group relative my-4 w-full overflow-hidden rounded-2xl border border-border bg-card'
          : 'group relative my-4 w-full overflow-hidden rounded-2xl border border-slate-200 bg-white'
      }
    >
      <div
        className={
          isDark
            ? 'flex items-center justify-between border-b border-border bg-muted/40 px-4 py-2'
            : 'flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2'
        }
      >
        <span
          className={
            isDark
              ? 'text-[11px] font-semibold tracking-[0.14em] text-muted-foreground'
              : 'text-[11px] font-semibold tracking-[0.14em] text-slate-500'
          }
        >
          {languageLabel}
        </span>
        <CodeCopyButton code={children} className="static" dark={isDark} />
      </div>
      <div className="overflow-x-auto">
        <SyntaxHighlighter
          PreTag="div"
          language={language}
          style={isDark ? oneDark : oneLight}
          wrapLines={false}
          wrapLongLines={false}
          customStyle={{
            maxHeight: 560,
            borderRadius: 0,
            margin: 0,
            background: 'transparent',
            padding: '0.9rem 1rem 1rem',
            fontSize: '13px',
            lineHeight: '1.7',
          }}
          codeTagProps={{
            style: {
              fontFamily:
                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, monospace',
            },
          }}
        >
          {children}
        </SyntaxHighlighter>
      </div>
    </div>
  );
}
